import moment from "moment";
import * as texts from "../StationEditDrawer/locales";
import { formatarMoeda } from "../FormatingUtils";

type TCostPerHourRange = {
  value?: number | string | null;
  start_time?: string | moment.Moment | null;
  end_time?: string | moment.Moment | null;
};

type TCostPerHourConfiguration = {
  timezone?: string | null;
  day_of_week?: string[];
  peak_value?: TCostPerHourRange | null;
  intermediary_peak_values?: TCostPerHourRange[] | null;
};

export const formatCurrencyValue = (value: any) => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return formatarMoeda(String(value));
  }

  return undefined;
};

export const parseCurrencyInput = (value: any) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const sanitized = String(value).replace(/[.,]/g, "");
  const parsed = parseInt(sanitized, 10);

  if (Number.isNaN(parsed)) {
    return null;
  }

  return parsed;
};

export const getCostPerHourInitialValues = (
  configuration?: TCostPerHourConfiguration | null
) => {
  if (!configuration) {
    return {
      timezone: moment.tz.guess(),
      day_of_week: [],
      peak_value: {},
      intermediary_peak_values: [],
    };
  }

  const timezone = configuration.timezone || moment.tz.guess();
  const day_of_week = configuration.day_of_week || [];
  const peak_value = configuration.peak_value
    ? {
        value: formatCurrencyValue(configuration.peak_value.value),
        start_time: configuration.peak_value.start_time
          ? moment(configuration.peak_value.start_time, "HH:mm")
          : null,
        end_time: configuration.peak_value.end_time
          ? moment(configuration.peak_value.end_time, "HH:mm")
          : null,
      }
    : {};

  const intermediary_peak_values =
    configuration.intermediary_peak_values?.map((entry) => ({
      value: formatCurrencyValue(entry.value),
      start_time: entry.start_time
        ? moment(entry.start_time, "HH:mm")
        : null,
      end_time: entry.end_time ? moment(entry.end_time, "HH:mm") : null,
    })) || [];

  return {
    timezone,
    day_of_week,
    peak_value,
    intermediary_peak_values,
  };
};

export const detectCostPerHourOverlapKeys = (
  ranges: Array<{ key: string; start: moment.Moment; end: moment.Moment }>
) => {
  const ordered = [...ranges].sort((a, b) =>
    a.start.valueOf() - b.start.valueOf()
  );
  const overlaps = new Set<string>();
  let lastRange: { key: string; end: moment.Moment } | null = null;

  ordered.forEach((range) => {
    if (!lastRange) {
      lastRange = { key: range.key, end: range.end };
      return;
    }

    if (range.start.isBefore(lastRange.end)) {
      overlaps.add(range.key);
      overlaps.add(lastRange.key);

      if (range.end.isAfter(lastRange.end)) {
        lastRange = { key: range.key, end: range.end };
      }

      return;
    }

    lastRange = { key: range.key, end: range.end };
  });

  return overlaps;
};

type TNormalizeParams = {
  configuration?: TCostPerHourConfiguration | null;
  freire: any;
  parseCurrency?: (value: any) => number | null;
};

export const COST_PER_HOUR_PEAK_RANGE_KEY = "peak";

export const getCostPerHourIntermediaryRangeKey = (index: number) =>
  `intermediary-${index}`;

export const buildCostPerHourIntermediaryFieldPath = (
  index: number,
  field: "value" | "start_time" | "end_time"
) => `cost_per_hour_configuration.intermediary_peak_values[${index}].${field}`;

export class CostPerHourValidationError extends Error {
  fields: string[];

  constructor(message: string, fields: string[]) {
    super(message);
    this.fields = fields;
    this.name = "CostPerHourValidationError";
  }
}

export const normalizeCostPerHourConfiguration = ({
  configuration,
  freire,
  parseCurrency = parseCurrencyInput,
}: TNormalizeParams) => {
  if (!configuration) {
    throw new CostPerHourValidationError(
      freire(texts.WARNING_COST_PER_HOUR_PEAK_REQUIRED),
      [
        "cost_per_hour_configuration.peak_value.value",
        "cost_per_hour_configuration.peak_value.start_time",
        "cost_per_hour_configuration.peak_value.end_time",
      ]
    );
  }

  const timezone = configuration.timezone || moment.tz.guess();
  const day_of_week = configuration.day_of_week || [];

  if (!day_of_week.length) {
    throw new CostPerHourValidationError(
      freire(texts.WARNING_COST_PER_HOUR_DAYS_REQUIRED),
      ["cost_per_hour_configuration.day_of_week"]
    );
  }

  const peak = configuration.peak_value || {};
  const parsedPeakValue = parseCurrency(peak.value);
  const peakStart = peak.start_time ? moment(peak.start_time) : null;
  const peakEnd = peak.end_time ? moment(peak.end_time) : null;

  if (parsedPeakValue === null || parsedPeakValue < 0) {
    throw new CostPerHourValidationError(
      freire(texts.WARNING_COST_PER_HOUR_PEAK_REQUIRED),
      ["cost_per_hour_configuration.peak_value.value"]
    );
  }

  if (!peakStart || !peakStart.isValid()) {
    throw new CostPerHourValidationError(
      freire(texts.WARNING_COST_PER_HOUR_PEAK_REQUIRED),
      ["cost_per_hour_configuration.peak_value.start_time"]
    );
  }

  if (!peakEnd || !peakEnd.isValid()) {
    throw new CostPerHourValidationError(
      freire(texts.WARNING_COST_PER_HOUR_PEAK_REQUIRED),
      ["cost_per_hour_configuration.peak_value.end_time"]
    );
  }

  if (!peakStart.isBefore(peakEnd)) {
    throw new CostPerHourValidationError(
      freire(texts.COST_PER_HOUR_TIME_ORDER_HINT),
      [
        "cost_per_hour_configuration.peak_value.start_time",
        "cost_per_hour_configuration.peak_value.end_time",
      ]
    );
  }

  const normalizedIntermediaries: Array<{
    value: number;
    start_time: string;
    end_time: string;
  }> = [];
  const ranges: Array<{
    key: string;
    start: moment.Moment;
    end: moment.Moment;
    fields: string[];
  }> = [
    {
      key: COST_PER_HOUR_PEAK_RANGE_KEY,
      start: peakStart.clone(),
      end: peakEnd.clone(),
      fields: [
        "cost_per_hour_configuration.peak_value.start_time",
        "cost_per_hour_configuration.peak_value.end_time",
      ],
    },
  ];
  const intermediaryEntries = configuration.intermediary_peak_values || [];

  intermediaryEntries.forEach((entry, index) => {
    const parsedValue = parseCurrency(entry?.value);
    const startMoment = entry?.start_time ? moment(entry.start_time) : null;
    const endMoment = entry?.end_time ? moment(entry.end_time) : null;

    const hasAnyField =
      parsedValue !== null ||
      (startMoment && startMoment.isValid()) ||
      (endMoment && endMoment.isValid());

    if (!hasAnyField) {
      return;
    }

    if (parsedValue === null || parsedValue < 0) {
      throw new CostPerHourValidationError(
        freire(texts.WARNING_COST_PER_HOUR_INCOMPLETE_INTERMEDIARY),
        [buildCostPerHourIntermediaryFieldPath(index, 'value')]
      );
    }

    if (!startMoment || !startMoment.isValid()) {
      throw new CostPerHourValidationError(
        freire(texts.WARNING_COST_PER_HOUR_INCOMPLETE_INTERMEDIARY),
        [buildCostPerHourIntermediaryFieldPath(index, 'start_time')]
      );
    }

    if (!endMoment || !endMoment.isValid()) {
      throw new CostPerHourValidationError(
        freire(texts.WARNING_COST_PER_HOUR_INCOMPLETE_INTERMEDIARY),
        [buildCostPerHourIntermediaryFieldPath(index, 'end_time')]
      );
    }

    if (!startMoment.isBefore(endMoment)) {
      throw new CostPerHourValidationError(
        freire(texts.COST_PER_HOUR_TIME_ORDER_HINT),
        [
          buildCostPerHourIntermediaryFieldPath(index, 'start_time'),
          buildCostPerHourIntermediaryFieldPath(index, 'end_time'),
        ]
      );
    }

    normalizedIntermediaries.push({
      value: parsedValue,
      start_time: startMoment.format("HH:mm"),
      end_time: endMoment.format("HH:mm"),
    });

    ranges.push({
      key: getCostPerHourIntermediaryRangeKey(index),
      start: startMoment.clone(),
      end: endMoment.clone(),
      fields: [
        buildCostPerHourIntermediaryFieldPath(index, 'start_time'),
        buildCostPerHourIntermediaryFieldPath(index, 'end_time'),
      ],
    });
  });

  const overlappingRanges = detectCostPerHourOverlapKeys(ranges);

  if (overlappingRanges.size > 0) {
    const fields = ranges
      .filter((range) => overlappingRanges.has(range.key))
      .flatMap((range) => range.fields);

    throw new CostPerHourValidationError(
      freire(texts.WARNING_COST_PER_HOUR_OVERLAP),
      fields
    );
  }

  return {
    timezone,
    day_of_week,
    peak_value: {
      value: parsedPeakValue,
      start_time: peakStart.format("HH:mm"),
      end_time: peakEnd.format("HH:mm"),
    },
    intermediary_peak_values: normalizedIntermediaries,
  };
};

export const hasDifferentCostPerHourValues = (
  energyCost: number | null | undefined,
  configuration?: {
    peak_value?: { value?: number | null } | null;
    intermediary_peak_values?: Array<{ value: number } | undefined>;
  } | null
) => {
  if (!configuration) {
    return false;
  }

  const values: number[] = [];

  if (typeof energyCost === "number") {
    values.push(energyCost);
  }

  if (typeof configuration.peak_value?.value === "number") {
    values.push(configuration.peak_value.value);
  }

  configuration.intermediary_peak_values?.forEach((entry) => {
    if (entry && typeof entry.value === "number") {
      values.push(entry.value);
    }
  });

  if (values.length <= 1) {
    return false;
  }

  return values.some((value) => value !== values[0]);
};
